import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Boxes,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  CircleOff,
  Clock3,
  LineChart,
  ListChecks,
  Network,
  QrCode,
  Store,
  Ticket,
  TriangleAlert,
  Users,
  Workflow,
  Layers,
  Sparkles,
  Terminal,
  ShieldCheck,
  Globe2,
  Waypoints,
} from "lucide-react";
import PublicNav, { Footer } from "@/components/PublicNav";
import { TypewriterDisclaimer } from "@/components/TypewriterDisclaimer";
import OkxDropdown from "@/components/OkxDropdown";
import { PLAN_META, PLAN_ORDER, priceFor } from "@/lib/pricing";
import { api, compact, num, DEMO_EVENT_ID, fetchCached } from "@/lib/api";
import { NodeIcon, colorOf as PREVIEW_COLOR } from "@/pages/workspace/BlueprintGraph";
import LiveTicker from "@/components/LiveTicker";
import {
  Reveal,
  RevealGroup,
  RevealItem,
  MaskReveal,
  ScrollProgressBar,
  HeroStageDepth,
  SpotlightCard,
  CounterNumber,
  ProcessCableRail,
} from "@/components/MotionPrimitives";
import {
  StitchAuroraBackground,
  StitchHeroCommandCapsule,
} from "@/components/StitchAtmosphere";

const HERO = "/assets/okkax-concert-hero-v2.png";

const PREVIEW_TONE = { get: (k) => PREVIEW_COLOR(k) };

const STATUS_META = {
  Confirmed: { color: "#ffffff", Icon: CheckCircle2, tooltip: "Sudah siap dan telah dikonfirmasi." },
  Pending: { color: "#ffffff", Icon: Clock3, tooltip: "Sedang menunggu keputusan atau penyelesaian." },
  "At Risk": { color: "#ffffff", Icon: TriangleAlert, tooltip: "Memerlukan perhatian karena dapat menghambat event." },
  Missing: { color: "#ffffff", Icon: CircleOff, tooltip: "Komponen wajib belum tersedia." },
  Completed: { color: "#ffffff", Icon: BadgeCheck, tooltip: "Target komponen telah selesai dipenuhi." },
};
const STATUS_ORDER = ["Confirmed", "Pending", "At Risk", "Missing", "Completed"];

const GRAPH_COMPONENT_LABELS = {
  event: "Event ID",
  organizer: "Organizer",
  talent: "Talent",
  rider: "Rider",
  venue: "Venue",
  vendor: "Vendor",
  sponsor: "Sponsor",
  tenant: "Tenant",
  workforce: "Workforce",
  ticket: "Ticketing",
  funding: "Pendanaan",
};

const PREVIEW_EDGES = [
  { id: "event-organizer", source: "event", target: "organizer", label: "diselenggarakan oleh" },
  { id: "event-talent", source: "event", target: "talent", label: "program talent" },
  { id: "talent-rider", source: "talent", target: "rider", label: "mengaktifkan rider" },
  { id: "rider-vendor", source: "rider", target: "vendor", label: "membutuhkan vendor" },
  { id: "event-venue", source: "event", target: "venue", label: "menggunakan venue" },
  { id: "venue-vendor", source: "venue", target: "vendor", label: "menentukan kebutuhan produksi" },
  { id: "venue-ticket", source: "venue", target: "ticket", label: "menentukan kapasitas tiket" },
  { id: "venue-workforce", source: "venue", target: "workforce", label: "menentukan crew & keamanan" },
  { id: "venue-tenant", source: "venue", target: "tenant", label: "menentukan kapasitas tenant" },
  { id: "event-sponsor", source: "event", target: "sponsor", label: "membuka inventory sponsor" },
  { id: "sponsor-funding", source: "sponsor", target: "funding", label: "menambah pendanaan" },
  { id: "funding-vendor", source: "funding", target: "vendor", label: "membuka aktivasi produksi" },
  { id: "event-tenant", source: "event", target: "tenant", label: "membuka zona tenant" },
  { id: "tenant-funding", source: "tenant", target: "funding", label: "menambah pendapatan" },
  { id: "event-workforce", source: "event", target: "workforce", label: "membutuhkan workforce" },
  { id: "vendor-workforce", source: "vendor", target: "workforce", label: "memicu crew & keamanan" },
  { id: "workforce-funding", source: "workforce", target: "funding", label: "menambah biaya operasi" },
  { id: "event-ticket", source: "event", target: "ticket", label: "membuka ticketing" },
  { id: "ticket-funding", source: "ticket", target: "funding", label: "menghasilkan pendapatan" },
  { id: "event-funding", source: "event", target: "funding", label: "mengendalikan anggaran" },
];

// affectedNodes: unique canonical nodes that causal propagation touches (denominator for simulation progress)
// preconditionKey: node id whose current status determines precondition warning
const SCENARIOS = [
  {
    id: "talent",
    title: "Talent dipilih",
    description: "Talent dipilih → rider aktif → vendor dibutuhkan → perjalanan dan akomodasi dihitung → keamanan diperbarui → anggaran berubah.",
    affectedNodes: ["talent", "rider", "vendor", "workforce", "funding"],
    preconditionKey: "talent",
    preconditionStatus: "Confirmed",
    preconditionWarning: "Talent sudah dikonfirmasi. Skenario ini berjalan sebagai simulasi hipotetis perubahan talent.",
    steps: [
      { label: "Talent dipilih", focus: "talent", nodes: ["event", "talent"], edges: ["event-talent"] },
      { label: "Rider aktif", focus: "rider", nodes: ["rider"], edges: ["talent-rider"] },
      { label: "Vendor dibutuhkan", focus: "vendor", nodes: ["vendor"], edges: ["rider-vendor"] },
      { label: "Perjalanan & akomodasi dihitung", focus: "vendor", nodes: ["vendor"], edges: ["rider-vendor"] },
      { label: "Keamanan diperbarui", focus: "workforce", nodes: ["workforce"], edges: ["vendor-workforce"] },
      { label: "Anggaran berubah", focus: "funding", nodes: ["funding"], edges: ["workforce-funding"] },
    ],
    // Scenario state per affected node (baseline → scenario, for inspector overlay)
    nodeScenarioStates: {
      talent:    { scenarioStatus: "HYPOTHETICAL_CHANGE", reason: "Perubahan talent memerlukan rider baru dan validasi ulang semua dependensi." },
      rider:     { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Rider harus dicocokkan ulang dengan talent baru." },
      vendor:    { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Kebutuhan produksi dan akomodasi bergantung pada rider aktif." },
      workforce: { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Keamanan dan crew bergantung pada kebutuhan vendor terbaru." },
      funding:   { scenarioStatus: "UNKNOWN", reason: "Dampak finansial tidak dapat dihitung tanpa data talent pengganti." },
    },
  },
  {
    id: "venue",
    title: "Venue berubah",
    description: "Venue berubah → kapasitas berubah → tiket dihitung ulang → workforce dan keamanan berubah → tenant dan pendapatan diperbarui.",
    affectedNodes: ["venue", "ticket", "workforce", "tenant", "funding"],
    preconditionKey: null, // always valid hypothetical
    preconditionStatus: null,
    preconditionWarning: null,
    steps: [
      { label: "Venue berubah", focus: "venue", nodes: ["event", "venue"], edges: ["event-venue"] },
      { label: "Kapasitas berubah", focus: "venue", nodes: ["venue"], edges: ["event-venue"] },
      { label: "Tiket dihitung ulang", focus: "ticket", nodes: ["ticket"], edges: ["venue-ticket"] },
      { label: "Workforce & keamanan berubah", focus: "workforce", nodes: ["workforce"], edges: ["venue-workforce"] },
      { label: "Tenant diperbarui", focus: "tenant", nodes: ["tenant"], edges: ["venue-tenant"] },
      { label: "Pendapatan diperbarui", focus: "funding", nodes: ["funding"], edges: ["tenant-funding"] },
    ],
    nodeScenarioStates: {
      venue:     { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Venue berubah memerlukan validasi ulang kapasitas, layout, dan akses." },
      ticket:    { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Kapasitas tiket mengikuti venue. Metrik penjualan tidak berubah tanpa data venue baru." },
      workforce: { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Kebutuhan crew dan keamanan bergantung pada venue aktif." },
      tenant:    { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Jumlah dan layout booth bergantung pada venue yang dipilih." },
      funding:   { scenarioStatus: "UNKNOWN", reason: "Dampak finansial tidak dapat dihitung tanpa data venue pengganti." },
    },
  },
  {
    id: "sponsor",
    title: "Sponsor dikonfirmasi",
    description: "Sponsor dikonfirmasi → pendanaan bertambah → funding gap berkurang → hak aktivasi dibuat → kebutuhan produksi bertambah.",
    affectedNodes: ["sponsor", "funding", "vendor"],
    preconditionKey: "sponsor",
    preconditionStatus: "Confirmed",
    preconditionWarning: "Sponsor sudah dikonfirmasi. Skenario ini berjalan sebagai simulasi hipotetis penambahan sponsor.",
    steps: [
      { label: "Sponsor dikonfirmasi", focus: "sponsor", nodes: ["event", "sponsor"], edges: ["event-sponsor"] },
      { label: "Pendanaan bertambah", focus: "funding", nodes: ["funding"], edges: ["sponsor-funding"] },
      { label: "Funding gap berkurang", focus: "funding", nodes: ["funding"], edges: ["sponsor-funding"] },
      { label: "Hak aktivasi dibuat", focus: "sponsor", nodes: ["sponsor"], edges: ["event-sponsor"] },
      { label: "Kebutuhan produksi bertambah", focus: "vendor", nodes: ["vendor"], edges: ["funding-vendor"] },
    ],
    nodeScenarioStates: {
      sponsor: { scenarioStatus: "HYPOTHETICAL_CHANGE", reason: "Penambahan sponsor sebagai simulasi hipotetis — nilai aktual tidak tersedia." },
      funding: { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Pendanaan perlu diverifikasi ulang dengan nilai sponsor baru." },
      vendor:  { scenarioStatus: "REVALIDATION_REQUIRED", reason: "Hak aktivasi sponsor dapat menambah kebutuhan produksi vendor." },
    },
  },
];

const PW = 900, PH = 560, PCX = 450, PCY = 275;
const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
const progressOf = (done, total) => total > 0 ? clampPercent((done / total) * 100) : 0;
const firstOpen = (items, fallback = "Tidak ada kebutuhan terbuka.") => items.length ? items : [fallback];

function buildPreviewNodes(summary, catalogEvent) {
  if (!summary) return [];
  const demoEvent = summary.event || {};
  const event = catalogEvent || demoEvent;
  const isDemoEvent = !catalogEvent?.id || catalogEvent.id === demoEvent.id;
  const key = summary.key_numbers || {};
  const networkData = summary.network || {};
  const operations = summary.operations || {};
  const brief = summary.brief || {};
  const rippleData = summary.ripple || {};

  const totalCost = Number(isDemoEvent ? key.total_event_cost : event.budget || 0);
  const confirmedFunding = Number(isDemoEvent ? key.confirmed_funding : 0);
  const fundingGap = isDemoEvent ? Number(key.funding_gap || 0) : null;
  const fundingProgress = isDemoEvent && totalCost > 0 ? progressOf(confirmedFunding, totalCost) : 0;
  const headline = isDemoEvent ? brief.headliner : event.headline_talent;
  const talentCount = Number(isDemoEvent ? networkData.talents : event.talent_count || 0);
  const riderMatched = Number(isDemoEvent ? operations.rider_matched : 0);
  const riderTotal = Number(isDemoEvent ? operations.rider_total : 0);
  const vendorConfirmed = Number(isDemoEvent ? operations.vendors_confirmed : event.vendor_count || 0);
  const vendorTotal = Number(isDemoEvent ? operations.vendors_total : event.vendor_count || 0);
  const workforceFilled = Number(isDemoEvent ? operations.workforce_filled : 0);
  const workforceNeeded = Number(isDemoEvent ? operations.workforce_needed : 0);
  const boothsOccupied = Number(isDemoEvent ? networkData.booths_occupied : event.tenant_count || 0);
  const boothsTotal = Number(isDemoEvent ? networkData.booths_total : event.tenant_count || 0);
  const ticketsSold = Number(isDemoEvent ? networkData.tickets_sold : event.sold || 0);
  const ticketCapacity = Number(isDemoEvent ? networkData.ticket_capacity : ticketsSold + Number(event.tickets_remaining || 0));
  const sponsorCommitments = Number(isDemoEvent ? networkData.sponsor_commitments : event.sponsor_sold || 0);
  const sponsorTiers = Number(isDemoEvent ? brief.sponsor_tiers : event.sponsor_slots || 0);
  const catalogSignals = [event.organizer_name, headline || talentCount, event.venue_name, ticketCapacity, vendorConfirmed, sponsorTiers];
  const readiness = isDemoEvent
    ? clampPercent(operations.readiness)
    : progressOf(catalogSignals.filter(Boolean).length, catalogSignals.length);
  const eventStatus = event.status === "completed" ? "Completed" : ["published", "live"].includes(event.status) ? "Confirmed" : "Pending";
  const riderStatus = !isDemoEvent ? (headline ? "Pending" : "Missing") : riderTotal === 0 ? "Missing" : riderMatched >= riderTotal ? "Completed" : riderMatched > 0 ? "Pending" : "At Risk";
  const vendorStatus = vendorTotal === 0 ? "Missing" : vendorConfirmed >= vendorTotal ? "Completed" : vendorConfirmed > 0 ? "Pending" : "At Risk";
  const workforceStatus = workforceNeeded === 0 ? "Missing" : workforceFilled >= workforceNeeded ? "Completed" : progressOf(workforceFilled, workforceNeeded) < 60 ? "At Risk" : "Pending";
  const tenantStatus = boothsTotal === 0 ? "Missing" : boothsOccupied >= boothsTotal ? "Completed" : boothsOccupied > 0 ? "Pending" : "Missing";
  const ticketStatus = ticketCapacity === 0 ? "Missing" : ticketsSold >= ticketCapacity ? "Completed" : "Pending";
  const sponsorStatus = sponsorTiers === 0 ? "Missing" : sponsorCommitments >= sponsorTiers ? "Completed" : sponsorCommitments > 0 ? "Confirmed" : "Pending";

  // ── Funding: canonical formula fix ──────────────────────────────────────────
  // fundingGap   = max(required - confirmed, 0) — always non-negative
  // fundingVariance = confirmed - required — signed (positive = surplus)
  // Coverage     = confirmed / required, clamped to 100%
  // Status semantics: FUNDED when variance >= 0, FUNDING_GAP_OPEN when < 0
  const fundingVariance = isDemoEvent ? (confirmedFunding - totalCost) : null;
  const fundingGapPositive = isDemoEvent ? Math.max(totalCost - confirmedFunding, 0) : 0;
  const fundingStatus = totalCost === 0
    ? "Missing"
    : isDemoEvent
      ? (fundingVariance !== null && fundingVariance >= 0 ? "Completed" : "At Risk")
      : "Pending";
  const fundingLabel = isDemoEvent
    ? (fundingGapPositive > 0
        ? `Funding Gap ${compact(fundingGapPositive)}`
        : (fundingVariance !== null && fundingVariance > 0
            ? `Surplus ${compact(fundingVariance)}`
            : "Fully Funded"))
    : `Anggaran ${compact(totalCost)}`;

  // ── Metric numerics (exposed for domain-specific labels in inspector) ────────
  const metricNumerics = {
    rider:     { done: riderMatched,      total: riderTotal,       metricType: "FULFILLMENT" },
    vendor:    { done: vendorConfirmed,   total: vendorTotal,      metricType: "COVERAGE" },
    tenant:    { done: boothsOccupied,    total: boothsTotal,      metricType: "OCCUPANCY" },
    workforce: { done: workforceFilled,   total: workforceNeeded,  metricType: "STAFFING" },
    ticket:    { done: ticketsSold,       total: ticketCapacity,   metricType: "TICKET SALES" },
    sponsor:   { done: sponsorCommitments,total: sponsorTiers,     metricType: "COMMITMENT" },
    funding:   { done: confirmedFunding,  total: totalCost,        metricType: "FUNDING COVERAGE" },
    talent:    { done: talentCount,       total: Math.max(talentCount, 1), metricType: "BOOKING STATUS" },
    organizer: { done: event.organizer_name ? 1 : 0, total: 1,    metricType: "BOOKING STATUS" },
    venue:     { done: event.venue_name ? 1 : 0,      total: 1,    metricType: "AGREEMENT STATUS" },
    event:     { done: readiness,         total: 100,              metricType: "READINESS" },
  };

  return [
    {
      id: "event", label: event.name || "Event Okkax", kind: "Event", status: eventStatus,
      description: "Pusat kendali yang menghubungkan semua komponen pada satu Event ID.",
      owner: event.organizer_name || "Event Organizer",
      value: `${event.event_code || DEMO_EVENT_ID} · ${compact(totalCost)}`,
      metricType: "READINESS",
      metricDone: readiness, metricTotal: 100,
      progress: readiness,
      unmet: readiness < 100 ? [`${100 - readiness}% komponen katalog masih perlu dilengkapi.`] : [],
      risks: fundingGapPositive > 0 ? [`Funding gap ${compact(fundingGapPositive)} masih terbuka.`] : [],
      nextAction: readiness < 100 ? "Buka komponen berstatus Pending, At Risk, atau Missing dan selesaikan prioritasnya." : "Pertahankan kesiapan dan pantau perubahan komponen.",
    },
    {
      id: "organizer", label: event.organizer_name || "Organizer", kind: "Organizer",
      status: event.organizer_name ? "Confirmed" : "Missing",
      description: "Pemilik keputusan utama yang mengarahkan brief, approval, dan kesiapan event.",
      owner: event.organizer_name || "Belum tersedia",
      value: event.event_code || "Event ID belum tersedia",
      metricType: "BOOKING STATUS",
      metricDone: event.organizer_name ? 1 : 0, metricTotal: 1,
      progress: event.organizer_name ? 100 : 0,
      unmet: event.organizer_name ? [] : ["Penanggung jawab event belum tersedia."],
      risks: event.organizer_name ? [] : ["Keputusan lintas komponen tidak memiliki pemilik."],
      nextAction: event.organizer_name ? "Tinjau status komponen dan konfirmasi keputusan lintas tim." : "Tetapkan organizer penanggung jawab.",
    },
    {
      id: "talent", label: headline || `${num(talentCount)} talent`, kind: "Talent",
      status: headline || talentCount > 0 ? "Confirmed" : "Missing",
      description: "Program utama yang mengaktifkan rider, jadwal, perjalanan, keamanan, dan biaya terkait talent.",
      owner: "Talent Management",
      value: isDemoEvent && Number(brief.headliner_landed_cost || 0) > 0 ? compact(brief.headliner_landed_cost) : `${num(talentCount)} talent terhubung`,
      // BOOKING STATUS: binary confirmation metric, not a fulfillment ratio
      metricType: "BOOKING STATUS",
      metricDone: headline || talentCount > 0 ? 1 : 0, metricTotal: 1,
      progress: headline || talentCount > 0 ? 100 : 0,
      unmet: headline || talentCount > 0 ? [] : ["Talent utama belum dipilih."],
      risks: riderTotal > riderMatched ? [`${num(riderTotal - riderMatched)} item rider belum matched.`] : [],
      nextAction: riderTotal > riderMatched ? "Selesaikan rider yang belum matched dan konfirmasi kebutuhan talent." : "Pertahankan konfirmasi talent dan pantau perubahan rider.",
    },
    {
      id: "rider", label: isDemoEvent ? `Rider (${num(riderMatched)}/${num(riderTotal)})` : "Rider talent", kind: "Rider", status: riderStatus,
      description: "Daftar kebutuhan teknis, hospitality, perjalanan, akomodasi, dan keamanan talent.",
      owner: "Talent Management & Production",
      value: isDemoEvent ? `${num(riderMatched)} dari ${num(riderTotal)} item matched` : headline ? `Mengikuti ${headline}` : "Belum tersedia",
      metricType: "FULFILLMENT",
      metricDone: isDemoEvent ? riderMatched : 0, metricTotal: isDemoEvent ? riderTotal : 0,
      progress: isDemoEvent ? progressOf(riderMatched, riderTotal) : 0,
      unmet: isDemoEvent && riderTotal > riderMatched ? [`${num(riderTotal - riderMatched)} item rider belum terpenuhi.`] : !isDemoEvent ? ["Detail rider dikelola di workspace event."] : [],
      risks: riderStatus === "At Risk" ? ["Belum ada item rider yang dikonfirmasi matched."] : [],
      nextAction: riderTotal > riderMatched ? "Cocokkan item rider dengan vendor, perjalanan, dan akomodasi." : "Kunci rider dan monitor perubahan talent.",
    },
    {
      id: "venue", label: event.venue_name || "Venue belum dipilih", kind: "Venue",
      status: event.venue_name ? "Confirmed" : "Missing",
      description: "Lokasi event yang menentukan kapasitas, layout, ticketing, tenant, workforce, dan keamanan.",
      owner: "Venue Management",
      value: isDemoEvent && Number(rippleData.venue_income || 0) > 0 ? compact(rippleData.venue_income) : event.city || "Biaya belum tersedia",
      // AGREEMENT STATUS: binary — venue agreement confirmed or not
      metricType: "AGREEMENT STATUS",
      metricDone: event.venue_name ? 1 : 0, metricTotal: 1,
      progress: event.venue_name ? 100 : 0,
      unmet: event.venue_name ? [] : ["Venue belum dipilih."],
      risks: event.venue_name ? [] : ["Kapasitas dan kebutuhan operasi belum dapat dikunci."],
      nextAction: event.venue_name ? "Konfirmasi kapasitas, akses, layout, dan kebutuhan produksi venue." : "Pilih dan konfirmasi venue.",
    },
    {
      id: "vendor", label: isDemoEvent ? `Vendor (${num(vendorConfirmed)}/${num(vendorTotal)})` : `Vendor (${num(vendorConfirmed)})`, kind: "Vendor", status: vendorStatus,
      description: "Mitra produksi yang memenuhi kebutuhan teknis, logistik, perjalanan, akomodasi, dan layanan event.",
      owner: "Production Lead",
      value: isDemoEvent ? compact(rippleData.vendor_payout || 0) : `${num(vendorConfirmed)} vendor terhubung`,
      metricType: "COVERAGE",
      metricDone: vendorConfirmed, metricTotal: vendorTotal,
      progress: progressOf(vendorConfirmed, vendorTotal),
      unmet: vendorTotal > vendorConfirmed ? [`${num(vendorTotal - vendorConfirmed)} vendor belum dikonfirmasi.`] : [],
      risks: vendorStatus === "At Risk" ? ["Belum ada vendor yang terkonfirmasi."] : [],
      nextAction: vendorTotal > vendorConfirmed ? "Konfirmasi vendor prioritas dan cocokkan kebutuhan produksi." : "Pantau delivery dan perubahan scope vendor.",
    },
    {
      id: "sponsor", label: `Sponsor (${num(sponsorCommitments)})`, kind: "Sponsor", status: sponsorStatus,
      description: "Partner komersial yang menambah pendanaan dan mengaktifkan hak brand serta kebutuhan produksi.",
      owner: "Commercial & Partnership",
      value: isDemoEvent ? compact(networkData.sponsor_value || 0) : `${num(sponsorCommitments)} dari ${num(sponsorTiers)} slot terisi`,
      metricType: "COMMITMENT",
      metricDone: sponsorCommitments, metricTotal: sponsorTiers,
      progress: progressOf(sponsorCommitments, sponsorTiers),
      unmet: sponsorTiers > sponsorCommitments ? [`${num(sponsorTiers - sponsorCommitments)} inventory sponsor belum terisi.`] : [],
      risks: sponsorCommitments === 0 ? ["Pendanaan sponsor belum terkonfirmasi."] : [],
      nextAction: sponsorTiers > sponsorCommitments ? "Tutup inventory sponsor dan detailkan hak aktivasi." : "Kunci hak aktivasi dan kebutuhan produksi sponsor.",
    },
    {
      id: "tenant", label: isDemoEvent ? `Tenant (${num(boothsOccupied)}/${num(boothsTotal)})` : `Tenant (${num(boothsOccupied)})`, kind: "Tenant", status: tenantStatus,
      description: "Peserta area komersial yang memakai booth dan menambah pendapatan serta kebutuhan operasi lokasi.",
      owner: "Tenant Operations",
      value: isDemoEvent ? compact(networkData.tenant_revenue || 0) : `${num(boothsOccupied)} tenant aktif`,
      metricType: "OCCUPANCY",
      metricDone: boothsOccupied, metricTotal: boothsTotal,
      progress: progressOf(boothsOccupied, boothsTotal),
      unmet: boothsTotal > boothsOccupied ? [`${num(boothsTotal - boothsOccupied)} booth belum terisi.`] : [],
      risks: tenantStatus === "Missing" ? ["Zona tenant belum menghasilkan okupansi."] : [],
      nextAction: boothsTotal > boothsOccupied ? "Isi booth yang tersedia dan konfirmasi kebutuhan tiap tenant." : "Finalisasi layout, akses, dan operasional tenant.",
    },
    {
      id: "workforce", label: isDemoEvent ? `Workforce (${num(workforceFilled)}/${num(workforceNeeded)})` : "Workforce", kind: "Worker", status: workforceStatus,
      description: "Crew lapangan dan keamanan yang menjalankan kebutuhan operasional sesuai kapasitas serta venue.",
      owner: "Operations & Security Lead",
      value: isDemoEvent ? compact(rippleData.workforce_payout || 0) : "Detail internal Event ID",
      metricType: "STAFFING",
      metricDone: workforceFilled, metricTotal: workforceNeeded,
      progress: progressOf(workforceFilled, workforceNeeded),
      unmet: workforceNeeded > workforceFilled ? [`${num(workforceNeeded - workforceFilled)} posisi belum terisi.`] : !isDemoEvent ? ["Rincian workforce tidak dipublikasikan di katalog."] : [],
      risks: workforceStatus === "At Risk" ? ["Kekurangan crew dapat mengganggu keamanan dan operasional show day."] : [],
      nextAction: workforceNeeded > workforceFilled ? "Isi posisi prioritas dan konfirmasi shift serta briefing keamanan." : "Kunci roster dan jadwal briefing.",
    },
    {
      id: "ticket", label: `Ticketing (${num(ticketsSold)}/${num(ticketCapacity)})`, kind: "Ticket tier", status: ticketStatus,
      description: "Kapasitas dan penjualan tiket yang mengikuti venue serta menjadi sumber pendapatan event.",
      owner: "Ticketing & Guest Experience",
      value: isDemoEvent ? compact(networkData.ticket_gmv || 0) : `${num(ticketsSold)} tiket terjual`,
      metricType: "TICKET SALES",
      metricDone: ticketsSold, metricTotal: ticketCapacity,
      progress: progressOf(ticketsSold, ticketCapacity),
      unmet: ticketCapacity > ticketsSold ? [`${num(ticketCapacity - ticketsSold)} tiket masih tersedia.`] : [],
      risks: ticketCapacity === 0 ? ["Ticket tier belum tersedia."] : [],
      nextAction: ticketCapacity === 0 ? "Buat dan aktifkan ticket tier." : "Pantau penjualan, kapasitas, dan kebutuhan akses masuk.",
    },
    {
      // Funding: corrected formula — gap is always non-negative, status matches variance sign
      id: "funding", label: fundingLabel, kind: "Funding",
      status: fundingStatus,
      description: "Ringkasan biaya, pendanaan terkonfirmasi, pendapatan, dan selisih kebutuhan event.",
      owner: "Finance Approver",
      value: isDemoEvent ? `${compact(confirmedFunding)} dari ${compact(totalCost)}` : compact(totalCost),
      metricType: "FUNDING COVERAGE",
      metricDone: isDemoEvent ? confirmedFunding : 0, metricTotal: totalCost,
      progress: fundingProgress,
      unmet: fundingGapPositive > 0 ? [`Funding gap ${compact(fundingGapPositive)} belum tertutup.`] : !isDemoEvent ? ["Rincian sumber pendanaan tersedia di workspace event."] : [],
      risks: fundingGapPositive > 0 ? ["Event masih memiliki kebutuhan pendanaan terbuka."] : [],
      nextAction: fundingGapPositive > 0 ? "Konfirmasi sponsor, tenant, ticketing, atau sesuaikan biaya prioritas." : "Tinjau anggaran dan sumber pendanaan pada Event ID.",
    },
  ];
}

function PricingPreview() {
  return (
    <section
      aria-labelledby="landing-pricing-heading"
      data-testid="landing-pricing-preview"
      className="border-b border-white/[0.06] bg-transparent px-4 py-10 sm:px-6 sm:py-16 md:py-24 font-gemini"
    >
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3.5 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-300 backdrop-blur-md">
              <span>Subscription Plans</span>
            </div>
            <h2 id="landing-pricing-heading" className="editorial mt-3 sm:mt-4 text-[clamp(1.75rem,4vw,3.4rem)] leading-[1.02] text-[#f4efec]">
              Start free. Scale with intelligence.
            </h2>
            <p className="mt-3 sm:mt-4 text-xs leading-relaxed text-zinc-400 sm:text-base">
              Free gets you into the network. Pro helps you operate professionally. Max adds advanced portfolio controls for optimization and scale.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid="landing-pricing-see-all"
            className="group inline-flex items-center gap-2 rounded-xl border border-white/[0.15] bg-white/[0.04] px-4 py-3 text-xs sm:px-5 sm:py-3.5 sm:text-sm font-semibold text-zinc-100 hover:border-white/[0.3] hover:bg-white/[0.08] transition-all"
          >
            <span>See all plans and roles</span>
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-6 sm:mt-10 grid gap-3.5 sm:gap-5 md:grid-cols-3">
          {PLAN_ORDER.map((planId) => {
            const meta = PLAN_META[planId];
            const price = priceFor("organizer", planId, "monthly");
            const isMax = planId === "max";
            return (
              <SpotlightCard
                key={planId}
                data-testid={`landing-pricing-${planId}`}
                spotlightColor={isMax ? "rgba(255, 46, 126, 0.12)" : "rgba(255, 255, 255, 0.08)"}
                className={[
                  "flex h-full flex-col rounded-2xl border p-4 sm:p-6 md:p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgba(0,0,0,0.8)]",
                  isMax
                    ? "border-white/[0.22] bg-gradient-to-b from-[#181824]/90 to-[#0e0e15]/90 shadow-[0_20px_50px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.12)]"
                    : "border-white/[0.08] bg-[#0c0c11]/85 backdrop-blur-xl shadow-md hover:border-white/25",
                ].join(" ")}
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="text-lg font-bold text-white">{meta.label}</h3>
                  <span className="rounded-md border border-white/[0.08] bg-white/[0.04] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 font-gemini-mono">
                    {meta.intelligence}
                  </span>
                </div>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-gemini-mono text-2xl sm:text-3xl font-bold text-white">{price.label}</span>
                  <span className="text-xs text-zinc-400">
                    {planId === "free" ? "forever" : "per month, Organizer"}
                  </span>
                </div>
                <p className="mt-3.5 text-sm text-zinc-400 leading-relaxed">{meta.positioning}</p>
                {meta.inherits && (
                  <div className="mt-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-[11px] text-zinc-300 font-medium">
                    {meta.inherits}
                  </div>
                )}
              </SpotlightCard>
            );
          })}
        </div>

        <div className="mt-6 text-xs text-zinc-500">
          Anchored on Organizer pricing. Talent, Venue, Vendor, Workforce, Sponsor, and Tenant have role-specific prices on the full pricing page.
        </div>
      </div>
    </section>
  );
}

function StatusPill({ status, testId }) {
  const meta = STATUS_META[status] || STATUS_META.Pending;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={testId}
      title={`${status}: ${meta.tooltip}`}
      aria-label={`${status}: ${meta.tooltip}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-[#111114]/90 px-3 py-1 text-[10.5px] font-bold tracking-wider text-white hover:border-white/30 hover:bg-white/[0.06] transition-all shadow-sm cursor-default"
    >
      <Icon size={12} strokeWidth={2.4} className="text-zinc-300" aria-hidden="true" />
      <span>{status}</span>
    </span>
  );
}

function GraphPreview() {
  const graphScrollRef = useRef(null);
  const [summary, setSummary] = useState(null);
  const [catalogEvents, setCatalogEvents] = useState([]);
  const [catalogTotals, setCatalogTotals] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(DEMO_EVENT_ID);
  const [loadError, setLoadError] = useState(false);
  const [active, setActive] = useState("event");
  const [hovered, setHovered] = useState(null);
  const [focused, setFocused] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [focusedEdge, setFocusedEdge] = useState(null);
  const [activeScenario, setActiveScenario] = useState(null);
  const [scenarioStep, setScenarioStep] = useState(-1);
  const [scenarioRun, setScenarioRun] = useState(0);
  // simulationState: separate causal propagation progress — NOT node metric
  // null = no simulation active; object = simulation running or complete
  const [simulationState, setSimulationState] = useState(null);
  // preconditionWarning: shown when scenario runs on already-confirmed node
  const [preconditionWarning, setPreconditionWarning] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadGraphData = async () => {
      try {
        const [sumData, eventsData] = await Promise.all([
          fetchCached("/demo/summary", 60_000).catch(() => null),
          fetchCached("/public/graph-events", 60_000).catch(() =>
            fetchCached("/discover/events", 60_000).catch(() => null)
          ),
        ]);
        if (!mounted) return;
        if (sumData && eventsData?.items?.length) {
          setSummary(sumData);
          setCatalogEvents(eventsData.items);
          setCatalogTotals(eventsData.totals || null);
        } else {
          setLoadError(true);
        }
      } catch {
        if (mounted) setLoadError(true);
      }
    };

    // Parallel immediate fetch on mount (zero artificial delay)
    loadGraphData();

    return () => { mounted = false; };
  }, []);

  const selectedCatalogEvent = useMemo(
    () => catalogEvents.find((event) => event.id === selectedEventId) || null,
    [catalogEvents, selectedEventId]
  );
  const catalogStats = useMemo(() => ({
    events: catalogTotals?.events ?? catalogEvents.length,
    promoters: catalogTotals?.promoters ?? new Set(catalogEvents.map((event) => event.organizer_name).filter(Boolean)).size,
    talents: catalogTotals?.talents ?? new Set(catalogEvents.map((event) => event.headline_talent).filter(Boolean)).size,
  }), [catalogEvents, catalogTotals]);
  // Dedupe by name supaya event dengan nama identik (seed backend hasilkan
  // beberapa nama yang berulang) tidak muncul dua kali di dropdown.
  const eventPickerOptions = useMemo(() => {
    if (!catalogEvents.length) {
      return [{ value: DEMO_EVENT_ID, label: "Event Okkax" }];
    }
    const seen = new Set();
    const options = [];
    for (const event of catalogEvents) {
      if (seen.has(event.name)) continue;
      seen.add(event.name);
      options.push({ value: event.id, label: event.name });
    }
    return options;
  }, [catalogEvents]);
  const graphNodes = useMemo(() => buildPreviewNodes(summary, selectedCatalogEvent), [summary, selectedCatalogEvent]);
  const nodeMap = useMemo(() => Object.fromEntries(graphNodes.map((node) => [node.id, node])), [graphNodes]);
  const positions = useMemo(() => {
    const outer = graphNodes.filter((node) => node.id !== "event");
    const output = {};
    const core = graphNodes.find((node) => node.id === "event");
    if (core) output.event = { x: PCX, y: PCY, a: 0, r: 34, node: core };
    outer.forEach((node, index) => {
      const angle = -Math.PI / 2 + (index / Math.max(1, outer.length)) * Math.PI * 2;
      output[node.id] = {
        x: PCX + Math.cos(angle) * 315,
        y: PCY + Math.sin(angle) * 226,
        a: angle,
        r: 20,
        node,
      };
    });
    return output;
  }, [graphNodes]);

  const scenario = SCENARIOS.find((item) => item.id === activeScenario);
  const scenarioState = useMemo(() => {
    const nodes = new Set();
    const edges = new Set();
    if (scenario && scenarioStep >= 0) {
      scenario.steps.slice(0, scenarioStep + 1).forEach((step) => {
        step.nodes.forEach((id) => nodes.add(id));
        step.edges.forEach((id) => edges.add(id));
      });
    }
    return { nodes, edges };
  }, [scenario, scenarioStep]);
  const scenarioEdgeId = scenario && scenarioStep >= 0 ? scenario.steps[scenarioStep]?.edges?.[0] : null;
  const inspectedEdgeId = hoveredEdge || focusedEdge || scenarioEdgeId || null;
  const inspectedEdge = PREVIEW_EDGES.find((edge) => edge.id === inspectedEdgeId) || null;

  useEffect(() => {
    const scroller = graphScrollRef.current;
    if (!scroller || graphNodes.length === 0) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (scroller.scrollWidth > scroller.clientWidth) {
        scroller.scrollLeft = Math.max(0, (scroller.scrollWidth - scroller.clientWidth) / 2);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [graphNodes.length]);

  useEffect(() => {
    if (!scenario) return undefined;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const affectedNodes = scenario.affectedNodes || [];
    const totalAffected = affectedNodes.length;
    if (reducedMotion) {
      const last = scenario.steps.length - 1;
      setScenarioStep(last);
      setActive(scenario.steps[last].focus);
      // Immediately mark simulation complete in reduced-motion mode
      setSimulationState((prev) => prev ? {
        ...prev,
        status: "COMPLETE",
        evaluated_nodes: totalAffected,
        progress_percent: 100,
      } : null);
      return undefined;
    }
    let step = 0;
    setScenarioStep(0);
    setActive(scenario.steps[0].focus);
    // Update simulation progress: map step index to affected node evaluation count
    // Each step advances toward evaluating all affectedNodes
    const updateSimProgress = (currentStep) => {
      const stepsTotal = scenario.steps.length;
      // evaluated_nodes proportional to step progress over total_affected_nodes
      const evaluated = Math.round((currentStep / Math.max(stepsTotal - 1, 1)) * totalAffected);
      const clampedEval = Math.min(evaluated, totalAffected);
      const pct = totalAffected > 0 ? Math.round((clampedEval / totalAffected) * 100) : 0;
      setSimulationState((prev) => prev ? {
        ...prev,
        status: clampedEval >= totalAffected ? "COMPLETE" : "RUNNING",
        evaluated_nodes: clampedEval,
        progress_percent: pct,
      } : null);
    };
    updateSimProgress(0);
    const timer = window.setInterval(() => {
      step += 1;
      if (step >= scenario.steps.length) {
        window.clearInterval(timer);
        // Final evaluation: all affected nodes evaluated
        setSimulationState((prev) => prev ? {
          ...prev,
          status: "COMPLETE",
          evaluated_nodes: totalAffected,
          progress_percent: 100,
        } : null);
        return;
      }
      setScenarioStep(step);
      setActive(scenario.steps[step].focus);
      updateSimProgress(step);
    }, 720);
    return () => window.clearInterval(timer);
  }, [scenario, scenarioRun]);

  const selected = nodeMap[active] || graphNodes[0];
  const inspectNode = hovered || focused || (active === "event" ? null : active);
  const linkedNodes = new Set(
    PREVIEW_EDGES
      .filter((edge) => inspectNode && (edge.source === inspectNode || edge.target === inspectNode))
      .flatMap((edge) => [edge.source, edge.target])
  );
  const relationships = selected
    ? PREVIEW_EDGES
        .filter((edge) => edge.source === selected.id || edge.target === selected.id)
        .map((edge) => ({
          ...edge,
          direction: edge.source === selected.id ? "Mendorong" : "Menerima dari",
          counterpart: GRAPH_COMPONENT_LABELS[edge.source === selected.id ? edge.target : edge.source] || "Komponen terkait",
        }))
    : [];

  const chooseNode = (id) => {
    setActiveScenario(null);
    setScenarioStep(-1);
    setSimulationState(null);
    setPreconditionWarning(null);
    setActive(id);
  };
  const playScenario = (id) => {
    const scenarioDef = SCENARIOS.find((s) => s.id === id);
    if (!scenarioDef) return;
    // Precondition check: warn if node already in preconditionStatus
    let warning = null;
    if (scenarioDef.preconditionKey && scenarioDef.preconditionStatus) {
      const preconditionNode = nodeMap[scenarioDef.preconditionKey];
      if (preconditionNode && preconditionNode.status === scenarioDef.preconditionStatus) {
        warning = scenarioDef.preconditionWarning;
      }
    }
    setPreconditionWarning(warning);
    // Init simulation state: evaluated_nodes starts at 0, progress at 0%
    setSimulationState({
      scenario_id: id,
      status: "RUNNING",
      total_affected_nodes: scenarioDef.affectedNodes.length,
      evaluated_nodes: 0,
      progress_percent: 0,
      affected_nodes: scenarioDef.affectedNodes,
      nodeScenarioStates: scenarioDef.nodeScenarioStates || {},
    });
    setActiveScenario(id);
    setScenarioStep(-1);
    setScenarioRun((run) => run + 1);
  };
  const resetSimulation = () => {
    // Full baseline restoration: clear all simulation state, return to canonical
    setActiveScenario(null);
    setScenarioStep(-1);
    setSimulationState(null);
    setPreconditionWarning(null);
    setActive("event");
  };
  const chooseEvent = (eventId) => {
    setSelectedEventId(eventId);
    setActiveScenario(null);
    setScenarioStep(-1);
    setSimulationState(null);
    setPreconditionWarning(null);
    setActive("event");
  };
  const graphEvent = selectedCatalogEvent || summary?.event || {};

  // ── Event Graph Completion ──────────────────────────────────────────
  // RESOLVED = Confirmed or Completed
  // INCOMPLETE = Pending, At Risk, Missing
  // NOT_APPLICABLE nodes: none in demo (all 10 are applicable)
  // Denominator: all applicable (= all 10)
  const RESOLVED_STATUSES = new Set(["Confirmed", "Completed"]);
  const graphCompletionStats = useMemo(() => {
    if (!graphNodes.length) return null;
    // Exclude Event center node from required dependency count (it’s the root container)
    const applicable = graphNodes.filter((n) => n.id !== "event");
    const resolved = applicable.filter((n) => RESOLVED_STATUSES.has(n.status));
    const incomplete = applicable.filter((n) => !RESOLVED_STATUSES.has(n.status));
    const pct = applicable.length > 0 ? Math.round((resolved.length / applicable.length) * 100) : 0;
    return {
      applicable: applicable.length,
      resolved: resolved.length,
      incomplete: incomplete.length,
      pct,
      isComplete: incomplete.length === 0,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphNodes]);

  if (!summary && !loadError) {
    return (
      <div data-testid="graph-preview-loading" className="grid gap-4 lg:grid-cols-[1.55fr_0.85fr]">
        <div className="h-[540px] animate-pulse border border-white/[0.08] bg-[#0c0c0e]" />
        <div className="h-[540px] animate-pulse border border-white/[0.08] bg-[#0c0c0e]" />
      </div>
    );
  }

  if (loadError || !selected) {
    return (
      <div data-testid="graph-preview-error" className="border border-[var(--okx-border)] bg-[var(--okx-surface)] p-8 text-sm text-zinc-400">
        Data Event Graph belum dapat dimuat. Muat ulang halaman untuk mencoba kembali.
      </div>
    );
  }

  return (
    <div className="space-y-3.5 sm:space-y-5 font-gemini" data-testid="graph-preview">
      {/* Event Switcher & Catalog Stats */}
      <div className="sticky top-[65px] z-30" data-testid="graph-event-switcher-sticky">
      <SpotlightCard className="overflow-visible" data-testid="graph-event-switcher">
        <div className="grid xl:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.75fr)]">
          <div className="p-3 sm:p-4">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-[0.2em] text-zinc-300 font-gemini-mono shadow-sm">
              <Sparkles size={10} className="text-zinc-400" />
              Event Aktif
            </div>
            <OkxDropdown
              value={selectedEventId}
              onChange={chooseEvent}
              options={eventPickerOptions}
              placeholder="Event Okkax"
              testId="graph-event-select"
              className="mt-1.5"
            />
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] text-zinc-400 font-gemini">
              <span data-testid="graph-active-organizer">{graphEvent.organizer_name || "Organizer belum tersedia"}</span>
              <span>· {graphEvent.city || "Kota belum tersedia"}</span>
              <span data-testid="graph-active-talent">· {graphEvent.headline_talent || (nodeMap.talent?.progress > 0 ? "Talent terhubung" : "Talent belum tersedia")}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 border-t border-white/[0.08] xl:border-t-0 xl:border-l bg-white/[0.01]" data-testid="graph-catalog-stats">
            {[
              [catalogStats.events, "event"],
              [catalogStats.talents, "talent"],
              [catalogStats.promoters, "promotor"],
            ].map(([value, label]) => (
              <div key={label} className="flex min-w-0 flex-col justify-center border-l border-white/[0.06] px-2.5 py-2.5 first:border-l-0 sm:px-4 sm:py-3">
                <span className="font-mono text-lg font-bold text-white sm:text-2xl">{num(value)}</span>
                <span className="mt-0.5 text-[8.5px] font-bold uppercase tracking-[0.14em] text-zinc-500 sm:text-[10px] font-gemini-mono">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </SpotlightCard>
      </div>

      {/* Main 2-Column Section: Unified 520px Integrated Command Canvas */}
      <div className="grid gap-3.5 sm:gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.85fr)] items-stretch">
        {/* Left Column: Graph Canvas with Integrated Scenario Command Controls */}
        <SpotlightCard className="overflow-hidden flex flex-col justify-between h-[520px] max-h-[520px]" data-testid="graph-canvas-card">
          {/* Top Control Bar: Active Relationship Readout + Scenario Segmented Ribbon */}
          <div
            className="flex min-h-[42px] flex-col justify-center gap-1.5 border-b border-white/[0.08] bg-[#0e0e10]/95 px-3.5 py-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3 font-gemini shrink-0"
            data-testid="graph-relationship-readout"
            aria-live="polite"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="shrink-0 text-[8.5px] font-bold uppercase tracking-[0.18em] text-zinc-400 font-gemini-mono">Hubungan aktif</span>
              {inspectedEdge ? (
                <span className="text-xs font-semibold text-zinc-200 truncate" data-testid={`graph-preview-edge-label-${inspectedEdge.id}`}>
                  <span className="text-white font-bold">{GRAPH_COMPONENT_LABELS[inspectedEdge.source]}</span>
                  <span className="mx-1.5 text-zinc-500" aria-hidden="true">→</span>
                  <span className="text-white font-bold">{GRAPH_COMPONENT_LABELS[inspectedEdge.target]}</span>
                  <span className="mx-1.5 text-zinc-600" aria-hidden="true">·</span>
                  <span className="text-zinc-300">{inspectedEdge.label}</span>
                </span>
              ) : (
                <span className="text-[10px] text-zinc-400 truncate">Arahkan atau fokuskan garis untuk membaca ketergantungan.</span>
              )}
            </div>

            {/* Scenario Segmented Ribbon */}
            <div className="flex items-center gap-1 shrink-0" data-testid="graph-scenarios">
              <span className="text-[8px] font-bold uppercase tracking-wider text-zinc-400 font-gemini-mono mr-0.5 hidden md:inline">Simulasi:</span>
              {SCENARIOS.map((item, index) => {
                const isSelected = activeScenario === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`graph-scenario-${item.id}`}
                    aria-pressed={isSelected}
                    onClick={() => playScenario(item.id)}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9.5px] font-semibold transition-all cursor-pointer active:scale-[0.98] ${
                      isSelected
                        ? "border border-[#ff2e7e]/60 bg-white/[0.1] text-white shadow-sm font-bold"
                        : "border border-white/[0.08] bg-[#111113]/90 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
                    }`}
                  >
                    <span className="font-gemini-mono text-[8px] opacity-70">0{index + 1}</span>
                    <span>{item.title}</span>
                  </button>
                );
              })}
              {activeScenario && (
                <button
                  type="button"
                  data-testid="graph-simulation-reset"
                  onClick={resetSimulation}
                  className="rounded-md border border-white/[0.15] bg-white/[0.04] px-1.5 py-1 text-[8.5px] font-bold text-zinc-300 hover:bg-white/[0.08] transition-all font-gemini-mono uppercase tracking-widest cursor-pointer ml-0.5"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          <div ref={graphScrollRef} className="bg-[#080808] w-full flex-1 min-h-0 flex items-center justify-center overflow-hidden relative">
            <svg
              viewBox={`0 0 ${PW} ${PH}`}
              preserveAspectRatio="xMidYMid meet"
              className="block w-full h-full max-h-full"
              role="img"
              aria-label={`Event Graph interaktif untuk ${graphEvent.name || "event Okkax"}`}
              data-testid="graph-preview-canvas"
            >
              <defs>
                <radialGradient id="previewCore">
                  <stop offset="0%" stopColor="#ff2e7e" stopOpacity="0.45" />
                  <stop offset="70%" stopColor="#ff2e7e" stopOpacity="0.06" />
                  <stop offset="100%" stopColor="#ff2e7e" stopOpacity="0" />
                </radialGradient>
                <marker id="graphArrow" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,9 L9,4.5 z" fill="#71717a" />
                </marker>
                <marker id="graphArrowActive" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,9 L9,4.5 z" fill="#ff2e7e" />
                </marker>
              </defs>
              <rect width={PW} height={PH} fill="#080808" />
              <g opacity="0.14">
                {Array.from({ length: 27 }, (_, i) => <circle key={i} cx={(i * 109) % PW} cy={(i * 67) % PH} r="1" fill="#ffffff" />)}
              </g>
              <circle cx={PCX} cy={PCY} r={330} fill="url(#previewCore)" pointerEvents="none" />
              <ellipse cx={PCX} cy={PCY} rx="315" ry="226" fill="none" stroke="#ffffff" strokeOpacity="0.07" strokeDasharray="3 10" pointerEvents="none" />

              {PREVIEW_EDGES.map((edge) => {
                const source = positions[edge.source];
                const target = positions[edge.target];
                if (!source || !target) return null;
                const dx = target.x - source.x;
                const dy = target.y - source.y;
                const length = Math.max(1, Math.hypot(dx, dy));
                const sx = source.x + (dx / length) * (source.r + 4);
                const sy = source.y + (dy / length) * (source.r + 4);
                const tx = target.x - (dx / length) * (target.r + 10);
                const ty = target.y - (dy / length) * (target.r + 10);
                const radial = edge.source === "event" || edge.target === "event";
                const cx = (sx + tx) / 2 + (PCX - (sx + tx) / 2) * (radial ? 0 : 0.34);
                const cy = (sy + ty) / 2 + (PCY - (sy + ty) / 2) * (radial ? 0 : 0.34);
                const path = radial ? `M${sx},${sy} L${tx},${ty}` : `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
                const scenarioOn = scenarioState.edges.has(edge.id);
                const related = edge.source === inspectNode || edge.target === inspectNode;
                const edgeInspected = hoveredEdge === edge.id || focusedEdge === edge.id;
                const highlighted = scenario ? scenarioOn || edgeInspected : related || edgeInspected;
                return (
                  <g
                    key={edge.id}
                    data-testid={`graph-preview-edge-${edge.id}`}
                    data-active={edgeInspected || scenarioOn ? "true" : "false"}
                    tabIndex={0}
                    aria-label={`${GRAPH_COMPONENT_LABELS[edge.source]} menuju ${GRAPH_COMPONENT_LABELS[edge.target]}: ${edge.label}`}
                    onMouseEnter={() => setHoveredEdge(edge.id)}
                    onMouseLeave={() => setHoveredEdge(null)}
                    onFocus={() => setFocusedEdge(edge.id)}
                    onBlur={() => setFocusedEdge(null)}
                    style={{ cursor: "help", outline: "none" }}
                  >
                    <path d={path} fill="none" stroke="transparent" strokeWidth="14" pointerEvents="stroke" />
                    <path
                      d={path}
                      fill="none"
                      stroke={highlighted ? "#ff2e7e" : "#71717a"}
                      strokeWidth={highlighted ? 1.8 : 0.85}
                      strokeOpacity={scenario && !scenarioOn ? 0.07 : highlighted ? 0.9 : 0.24}
                      markerEnd={highlighted ? "url(#graphArrowActive)" : "url(#graphArrow)"}
                      style={{ transition: "stroke .25s ease, stroke-opacity .25s ease, stroke-width .25s ease" }}
                    />
                    {highlighted && (
                      <path
                        d={path}
                        fill="none"
                        stroke="#ff2e7e"
                        strokeWidth="2.4"
                        strokeDasharray="8 14"
                        strokeLinecap="round"
                        pointerEvents="none"
                      >
                        <animate attributeName="stroke-dashoffset" values="44;0" dur="1.2s" repeatCount="indefinite" />
                      </path>
                    )}
                  </g>
                );
              })}

              {Object.entries(positions).map(([id, position]) => {
                const node = position.node;
                const isCore = id === "event";
                const isActive = active === id;
                const isScenarioOn = scenarioState.nodes.has(id);
                const faded = scenario
                  ? scenarioStep >= 0 && !isScenarioOn
                  : inspectNode && !isActive && !linkedNodes.has(id);
                const radius = position.r;
                const status = STATUS_META[node.status] || STATUS_META.Pending;
                const StatusIcon = status.Icon;
                const componentLabel = GRAPH_COMPONENT_LABELS[id] || node.kind;
                return (
                  <g
                    key={id}
                    data-testid={`graph-preview-node-${id}`}
                    data-active={isActive ? "true" : "false"}
                    data-status={node.status}
                    transform={`translate(${position.x},${position.y})`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    aria-label={`${componentLabel}. Status ${node.status}. Klik untuk membuka Detail Komponen.`}
                    onMouseEnter={() => setHovered(id)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setFocused(id)}
                    onBlur={() => setFocused(null)}
                    onClick={() => chooseNode(id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        chooseNode(id);
                      }
                    }}
                    style={{ cursor: "pointer", opacity: faded ? 0.2 : 1, transition: "opacity .25s ease", outline: "none" }}
                  >
                    {(isCore || isActive || isScenarioOn || focused === id) && (
                      <circle r={radius + 7} fill="none" stroke={isScenarioOn ? "#ff2e7e" : status.color} strokeOpacity="0.72" strokeWidth="1.5" />
                    )}
                    <circle
                      r={radius}
                      fill={isCore ? "#ff2e7e" : "#0f0f11"}
                      stroke={isActive || isScenarioOn ? "#ffffff" : status.color}
                      strokeWidth={isCore || isActive || isScenarioOn ? 2.4 : 1.5}
                    />
                    <NodeIcon kind={node.kind} size={isCore ? 17 : 13} color={isCore ? "#0a0a0a" : PREVIEW_TONE.get(node.kind)} />
                    {!isCore && (
                      <g>
                        <circle cx={radius * 0.72} cy={-radius * 0.72} r="7.5" fill={status.color} stroke="#080808" strokeWidth="2" />
                        <StatusIcon x={radius * 0.72 - 4.75} y={-radius * 0.72 - 4.75} width="9.5" height="9.5" color="#080808" strokeWidth={2.6} aria-hidden="true" />
                      </g>
                    )}
                    {!isCore && (
                      <g pointerEvents="none">
                        <rect
                          x="-43"
                          y={radius + 7}
                          width="86"
                          height="31"
                          rx="6"
                          fill="#0c0c0e"
                          stroke={isActive || isScenarioOn ? "#ffffff" : "#27272a"}
                          strokeWidth="0.8"
                        />
                        <text x="0" y={radius + 19} textAnchor="middle" fontSize="8.6" fontWeight="700" fill={isActive || isScenarioOn ? "#ffffff" : "#d4d4d8"}>
                          {componentLabel}
                        </text>
                        <text x="0" y={radius + 30} textAnchor="middle" fontSize="6.7" fontWeight="700" letterSpacing="0.06em" fill={status.color}>
                          {node.status.toUpperCase()}
                        </text>
                      </g>
                    )}
                    {isCore && (
                      <g pointerEvents="none">
                        <text x="0" y="-16" textAnchor="middle" fontSize="5.8" fontWeight="800" letterSpacing="0.16em" fill="#0a0a0a">SATU</text>
                        <text x="0" y="21" textAnchor="middle" fontSize="6.4" fontWeight="800" letterSpacing="0.12em" fill="#0a0a0a">EVENT ID</text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Bottom Telemetry & Status Bar: Compact Single-Line Footer */}
          <div className="border-t border-white/[0.08] bg-[#0a0a0c]/95 px-3.5 py-2 shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[9.5px] font-gemini">
            {simulationState?.status === "COMPLETE" ? (
              <div data-testid="graph-simulation-complete" className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={13} className="text-white" aria-hidden="true" />
                  <span className="font-bold text-white font-gemini-mono uppercase tracking-wider text-[9px]">Simulation Complete</span>
                  <span className="text-zinc-400">·</span>
                  <span className="text-zinc-300 font-semibold">{scenario?.title}</span>
                  <span className="text-zinc-500 font-mono">({simulationState.evaluated_nodes}/{simulationState.total_affected_nodes} nodes dievaluasi)</span>
                </div>
                <button
                  type="button"
                  data-testid="graph-simulation-reset"
                  onClick={resetSimulation}
                  className="text-[8.5px] font-bold text-zinc-400 hover:text-white font-gemini-mono uppercase tracking-wider underline cursor-pointer"
                >
                  Tutup Simulasi
                </button>
              </div>
            ) : simulationState ? (
              <div className="flex items-center justify-between w-full" data-testid="graph-simulation-progress">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white font-gemini-mono text-[9px] uppercase tracking-wider">Simulasi Berjalan:</span>
                  <span className="text-zinc-200">{scenario?.title}</span>
                  <span className="text-zinc-400 font-mono text-[9px]">· {simulationState.evaluated_nodes}/{simulationState.total_affected_nodes} nodes ({simulationState.progress_percent}%)</span>
                </div>
                {preconditionWarning && (
                  <span className="text-[8.5px] text-zinc-400 italic hidden md:inline" data-testid="graph-precondition-warning">
                    {preconditionWarning}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-zinc-400">Klik node untuk detail · arah panah menunjukkan komponen yang terdampak.</span>
                <span className="font-mono text-zinc-400 font-semibold">Event ID: {graphEvent.event_code || DEMO_EVENT_ID}</span>
              </div>
            )}
          </div>
        </SpotlightCard>

        {/* Right Column: Component Detail Sidebar (Unified 520px Height with Internal Scroll) */}
        <SpotlightCard className="p-3.5 sm:p-4.5 h-[520px] max-h-[520px] flex flex-col justify-between overflow-hidden" data-testid="graph-preview-detail">
          <div className="overflow-y-auto pr-1 space-y-2 flex-1 min-h-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-[0.2em] text-zinc-300 font-gemini-mono">
                  Detail Komponen
                </div>
                <h3 data-testid="graph-detail-name" className="mt-1 text-base font-bold text-white tracking-tight sm:text-lg">{selected.label}</h3>
                <p className="mt-0.5 text-[10.5px] text-zinc-400 font-gemini">{selected.kind}</p>
              </div>
              <StatusPill status={selected.status} testId="graph-detail-status" />
            </div>

            {/* SIMULATION MODE overlay: shown when scenario is active for the selected node */}
            {(() => {
              if (!simulationState || !activeScenario) return null;
              const nodeSimState = simulationState.nodeScenarioStates?.[selected.id];
              const isAffected = simulationState.affected_nodes?.includes(selected.id);
              if (!isAffected) return null;
              return (
                <div className="rounded-lg border border-white/[0.15] bg-[#121214]/95 p-2" data-testid="graph-detail-simulation-overlay">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[8.5px] font-bold uppercase tracking-[0.2em] text-zinc-200 font-gemini-mono">Simulation Mode</span>
                    <span className="rounded-full bg-white/[0.08] border border-white/[0.1] px-1.5 py-0.5 text-[7.5px] font-mono font-bold text-zinc-300">{simulationState.progress_percent}%</span>
                  </div>
                  <dl className="space-y-0.5 text-[9.5px] font-gemini">
                    <div className="flex gap-2">
                      <dt className="shrink-0 text-zinc-500 font-gemini-mono text-[8px] uppercase tracking-wider w-16">Baseline</dt>
                      <dd className="text-zinc-200 font-semibold">{selected.status}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="shrink-0 text-zinc-500 font-gemini-mono text-[8px] uppercase tracking-wider w-16">Scenario</dt>
                      <dd className="text-white font-bold">{nodeSimState?.scenarioStatus || "REVALIDATION_REQUIRED"}</dd>
                    </div>
                    {nodeSimState?.reason && (
                      <div className="flex gap-2">
                        <dt className="shrink-0 text-zinc-500 font-gemini-mono text-[8px] uppercase tracking-wider w-16">Reason</dt>
                        <dd className="text-zinc-300 leading-tight">{nodeSimState.reason}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              );
            })()}

            <p data-testid="graph-detail-description" className="rounded-lg border-l-2 border-white/40 bg-white/[0.02] p-2 text-[10.5px] leading-snug text-zinc-300 font-gemini">
              {selected.description}
            </p>

            <dl className="grid gap-1.5 sm:grid-cols-2">
              {[
                ["Penanggung jawab", selected.owner, "owner"],
                ["Nilai / biaya", selected.value, "value"],
              ].map(([label, value, key]) => (
                <div key={key} className="rounded-lg border border-white/[0.08] bg-[#111113]/90 p-1.5">
                  <dt className="text-[8px] font-bold uppercase tracking-[0.14em] text-zinc-500 font-gemini-mono">{label}</dt>
                  <dd data-testid={`graph-detail-${key}`} className="mt-0.5 text-[10.5px] font-semibold leading-tight text-white font-gemini truncate">{value}</dd>
                </div>
              ))}
            </dl>

            {/* Node Metric: domain-specific label */}
            <div className="rounded-lg border border-white/[0.08] bg-[#111113]/90 p-2" data-testid="graph-detail-progress">
              <div className="flex items-center justify-between text-[8.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 font-gemini-mono">
                <span data-testid="graph-detail-metric-type">{selected.metricType || "METRIC"}</span>
                <span className="font-mono text-white font-bold">
                  {selected.metricTotal > 0 && selected.metricTotal !== 1
                    ? `${num(selected.metricDone)} / ${num(selected.metricTotal)}`
                    : `${selected.progress}%`}
                </span>
              </div>
              {selected.metricTotal > 1 && (
                <div className="mt-0.5 text-[7.5px] font-mono text-zinc-500">
                  {selected.progress}% — {num(selected.metricDone)} dari {num(selected.metricTotal)}
                </div>
              )}
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/[0.08]">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${selected.progress}%`, background: (STATUS_META[selected.status] || STATUS_META.Pending).color }} />
              </div>
            </div>

            <DetailList title="Kebutuhan belum terpenuhi" testId="graph-detail-unmet" items={firstOpen(selected.unmet)} />
            <DetailList title="Dependensi" testId="graph-detail-dependencies" items={relationships.map((edge) => `${edge.direction}: ${edge.counterpart} · ${edge.label}`)} />
            <DetailList title="Risiko" testId="graph-detail-risks" items={firstOpen(selected.risks, "Tidak ada risiko aktif pada data saat ini.")} />

            <div className="rounded-lg border border-white/[0.15] bg-[#141417]/95 p-2 shadow-sm" data-testid="graph-detail-next-action">
              <div className="flex items-center gap-1.5 text-[8.5px] font-bold uppercase tracking-[0.16em] text-zinc-200 font-gemini-mono">
                <ListChecks size={11} className="text-zinc-300" aria-hidden="true" /> Tindakan berikutnya
              </div>
              <p className="mt-0.5 text-[10.5px] leading-snug text-zinc-200 font-gemini">{selected.nextAction}</p>
            </div>
          </div>

          {/* Event Graph Completion: pinned at bottom */}
          {graphCompletionStats && (
            <div className="mt-2 pt-2 border-t border-white/[0.08] bg-[#0e0e10]/80 rounded-lg p-2 shrink-0" data-testid="graph-completion-widget">
              <div className="flex items-center justify-between">
                <div className="text-[8px] font-bold uppercase tracking-[0.14em] text-zinc-400 font-gemini-mono">Event Graph Completion</div>
                <span className={`text-[8.5px] font-bold font-gemini-mono ${
                  graphCompletionStats.isComplete ? "text-white" : "text-zinc-300"
                }`}>{graphCompletionStats.pct}%</span>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/[0.08]">
                <div
                  className="h-full rounded-full transition-[width] duration-500 bg-white"
                  style={{ width: `${graphCompletionStats.pct}%` }}
                />
              </div>
              <div className="mt-1 text-[8px] text-zinc-500 font-gemini">
                {graphCompletionStats.resolved} / {graphCompletionStats.applicable} required nodes resolved
                {graphCompletionStats.incomplete > 0 && ` · ${graphCompletionStats.incomplete} incomplete`}
              </div>
            </div>
          )}
        </SpotlightCard>
      </div>

    </div>
  );
}

function DetailList({ title, items = [], testId }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0e0e10]/80 p-2" data-testid={testId}>
      <div className="text-[8px] font-bold uppercase tracking-[0.14em] text-zinc-400 font-gemini-mono">{title}</div>
      <ul className="mt-1 space-y-0.5 font-gemini max-h-[85px] overflow-y-auto pr-1">
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className="flex gap-1.5 text-[10.5px] leading-snug text-zinc-300">
            <ArrowRight size={9} className="mt-0.5 shrink-0 text-white/60" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const PROCESS_STEPS = [
  [Workflow, "Brief", "Guided Event Brief menangkap tujuan, kapasitas, anggaran, dan kebutuhan."],
  [Boxes, "Blueprint", "Blueprint Engine menyusun fase, workstream, requirement, dan risiko. Semua dapat diedit."],
  [Building2, "Network", "Talent, rider, venue, vendor, dan workforce dicocokkan dengan penjelasan skor."],
  [CircleDollarSign, "Live Operations", "Sponsor, tenant, tiket, pembayaran, settlement, dan dampak event diperbarui otomatis."],
];

export default function Landing() {
  const [networkTotals, setNetworkTotals] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const heroRef = useRef(null);
  const shouldReduce = useReducedMotion();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile, { passive: true });

    fetchCached("/public/graph-events", 60_000)
      .then((data) => setNetworkTotals(data?.totals || null))
      .catch(() => {});

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroParallaxY = useTransform(heroProgress, [0, 1], [0, shouldReduce || isMobile ? 0 : -35]);
  const heroBgScale = useTransform(heroProgress, [0, 1], [1, shouldReduce || isMobile ? 1 : 0.96]);

  return (
    <div className="min-h-screen bg-transparent w-full overflow-x-hidden">
      <ScrollProgressBar />
      <PublicNav />

      {/* 1. HERO COMMAND CENTER: Controlled Parallax Entrance */}
      <section ref={heroRef} className="relative overflow-hidden bg-transparent">
        <StitchAuroraBackground className="homepage-copilot-layout min-h-[75vh] lg:min-h-[720px] flex flex-col justify-center pt-4 sm:pt-8 pb-8 sm:pb-16 md:pb-24">
          <motion.div
            style={{
              y: heroParallaxY,
              scale: heroBgScale,
              willChange: "transform",
            }}
            className="homepage-copilot-content relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center"
          >
            {/* Live Network Telemetry Pill */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="homepage-copilot-idle-only mb-5 inline-flex items-center gap-2.5 rounded-full border border-white/[0.12] bg-[#0c0c16]/80 px-4 py-1.5 backdrop-blur-xl shadow-lg"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              <span className="font-mono text-[10.5px] sm:text-[11px] font-semibold tracking-wide text-zinc-300">
                {networkTotals ? (
                  <>
                    <span className="font-bold text-white">{num(networkTotals.cities)} KOTA TERKONEKSI</span>
                    {" · "}{num(networkTotals.events)} EVENT TERHUBUNG
                    <span className="hidden sm:inline"> · {compact(networkTotals.economic_activity)} TOTAL AKTIVITAS EVENT</span>
                  </>
                ) : "MEMUAT TELEMETRI JARINGAN"}
              </span>
            </motion.div>

            {/* Core Tag */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="homepage-copilot-idle-only"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] px-3.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.2em] text-zinc-300 font-gemini-mono shadow-sm">
                <Sparkles size={11} className="text-zinc-400" />
                Live Event Operating Network
              </span>
            </motion.div>

            {/* Google Stitch-Grade Hero Command Capsule */}
            <motion.div
              initial={{ opacity: 0, y: 36, scale: 0.98 }}
              animate={{ opacity: 1, y: 12, scale: 1 }}
              transition={{ duration: 0.65, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="homepage-copilot-capsule-wrap mb-3 mt-6 w-full"
            >
              <StitchHeroCommandCapsule
                heroIntro={
                  <>
                    <MaskReveal delay={0.15}>
                      <h1 className="editorial text-[clamp(2.15rem,6.5vw,5.5rem)] font-bold leading-[0.98] tracking-[-0.045em] text-white">
                        Every moving part,
                        <br />
                        <span className="text-zinc-300 font-normal">working as one.</span>
                      </h1>
                    </MaskReveal>
                    <Reveal delay={0.25} y={12}>
                      <p className="mt-5 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base font-gemini font-normal">
                        OKKAX menghubungkan promotor, penyelenggara acara, talent, venue, vendor, tenaga kerja,
                        sponsor, tenant, ticketing, dan sistem keuangan dalam satu ekosistem live event yang terintegrasi.
                      </p>
                    </Reveal>
                  </>
                }
              />
            </motion.div>

            {/* Secondary Trust & Calendar Row */}
            <div className="mt-2 flex min-h-8 w-full max-w-4xl flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center text-[10.5px] text-zinc-400 font-gemini sm:text-[11px]">
              <TypewriterDisclaimer speed={12} />
              <span className="text-zinc-500 hidden sm:inline">·</span>
              <div className="flex items-center gap-1.5 text-zinc-300">
                <CalendarDays size={12} className="text-zinc-400" aria-hidden="true" />
                <a
                  href={`/calendar?date=${[
                    new Date().getFullYear(),
                    String(new Date().getMonth() + 1).padStart(2, "0"),
                    String(new Date().getDate()).padStart(2, "0"),
                  ].join("-")}`}
                  data-testid="hero-today-calendar-link"
                  title="Buka kalender hari ini"
                  className="transition-colors hover:text-white font-medium"
                >
                  {new Intl.DateTimeFormat("id-ID", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }).format(new Date())}
                </a>
              </div>
            </div>
          </motion.div>
        </StitchAuroraBackground>
      </section>

      {/* 2. LIVE NETWORK TICKER */}
      <LiveTicker />

      {/* 3. CARA OKKAX BEKERJA & EVENT GRAPH */}
      <section className="border-b border-white/[0.06] bg-[#080808]/60 backdrop-blur-md px-3.5 py-6 sm:px-6 sm:py-8">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="text-[10px] sm:text-[10.5px] font-semibold uppercase tracking-[0.2em] accent-text">Cara Okkax bekerja</p>
            <h2 className="editorial mt-1 text-lg sm:text-2xl text-white">Dari ide menjadi panggung yang hidup.</h2>
          </Reveal>

          <div className="mt-3 sm:mt-5">
            <RevealGroup stagger={0.06} className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
              {[
                {
                  title: "Brief & Blueprint",
                  description: "Input konsep acara, konversi menjadi Event Blueprint 6 fase terstruktur.",
                  Icon: Layers,
                },
                {
                  title: "Supply Matching",
                  description: "Temukan talent, venue, vendor terverifikasi dengan score-matching.",
                  Icon: Network,
                },
                {
                  title: "Komersialisasi",
                  description: "Buka inventaris tiket dinamis, slot sponsor, dan seleksi tenant.",
                  Icon: Ticket,
                },
                {
                  title: "Showtime & Settle",
                  description: "Validasi tiket offline di gate, pantau operasi, cairkan termin otomatis.",
                  Icon: CheckCircle2,
                },
              ].map(({ title, description, Icon }, index) => (
                <RevealItem key={title}>
                  <SpotlightCard
                    spotlightColor="rgba(255, 255, 255, 0.08)"
                    className="group h-full rounded-lg sm:rounded-xl border border-white/[0.08] bg-[#0c0c0e]/90 p-2 sm:p-3 shadow-md backdrop-blur-xl transition-all duration-200 hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_12px_28px_rgba(0,0,0,0.65)] active:scale-[0.98]"
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-1.5 sm:gap-2.5">
                      <div className="flex h-5.5 w-5.5 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-md sm:rounded-lg border border-white/[0.12] bg-white/[0.04] text-white">
                        <Icon size={11} aria-hidden="true" className="sm:h-3.5 sm:w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-gemini-mono text-[8px] sm:text-[9px] font-bold text-zinc-500">0{index + 1}</div>
                        <h3 className="text-[11px] sm:text-xs font-bold text-white transition-colors group-hover:text-zinc-100">{title}</h3>
                        <p className="mt-0.5 text-[9px] sm:text-[10px] leading-[1.35] text-zinc-400">{description}</p>
                      </div>
                    </div>
                  </SpotlightCard>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>

          <div className="mt-5 sm:mt-7">
            <GraphPreview />
          </div>
        </div>
      </section>

      {/* 4. CANONICAL PRODUCTS BENTO GRID */}
      <section id="products" className="border-b border-white/[0.06] bg-transparent px-3.5 py-6 sm:px-6 sm:py-12 md:py-16 font-gemini">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col justify-between gap-2.5 md:flex-row md:items-end">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.03] px-2.5 py-0.5 text-[9.5px] sm:text-[10.5px] font-bold uppercase tracking-[0.2em] text-zinc-300 backdrop-blur-md">
                <Sparkles size={11} className="text-zinc-400" />
                <span>Canonical Product Suite</span>
              </div>
              <h2 className="editorial mt-1.5 sm:mt-2 text-lg sm:text-2xl md:text-3xl text-white">
                Enam pilar operasional live event modern.
              </h2>
            </div>
            <p className="max-w-md text-[10.5px] sm:text-xs leading-relaxed text-zinc-300">
              Setiap produk terintegrasi langsung ke dalam Event Graph dan database jaringan aktif tanpa fragmentasi file atau spreadsheet terpisah.
            </p>
          </div>

          <RevealGroup stagger={0.06} className="mt-4 sm:mt-6 grid grid-cols-2 gap-2 sm:gap-3.5 lg:grid-cols-3">
            {[
              {
                num: "01",
                slug: "event-studio",
                title: "Event Studio",
                badge: "AI Compiler",
                desc: "Kompilasi brief acara menjadi Event Blueprint 6 fase & dependensi Event Graph lengkap.",
                icon: Workflow,
                link: "/products/event-studio",
              },
              {
                num: "02",
                slug: "network",
                title: "Network Supply",
                badge: "Jaringan Nasional",
                desc: "Katalog publik terverifikasi untuk Talent, Venue, Vendor, Workforce, Sponsor, & Tenant.",
                icon: Globe2,
                link: "/products/network",
              },
              {
                num: "03",
                slug: "copilot",
                title: "Okkax Copilot",
                badge: "Observability",
                desc: "Analitik 3 tingkat: Observe telemetri, Understand blocker, dan Optimize efisiensi biaya.",
                icon: Sparkles,
                link: "/products/copilot",
              },
              {
                num: "04",
                slug: "ticket-studio",
                title: "Ticket Studio",
                badge: "5 Inventory Modes",
                desc: "Kelola 5 tier tiket fleksibel dengan proteksi bot & dynamic pricing anti-scalping.",
                icon: Ticket,
                link: "/products/ticket-studio",
              },
              {
                num: "05",
                slug: "livepass",
                title: "LivePass Access",
                badge: "Dynamic QR",
                desc: "Tiket dinamis anti screenshot & duplikasi. Gate validator offline scan <200ms.",
                icon: QrCode,
                link: "/products/livepass",
              },
              {
                num: "06",
                slug: "protected-payment",
                title: "Protected Payment",
                badge: "Milestone Escrow",
                desc: "Escrow berbasis milestone termin: DP, Soundcheck, dan Show selesai untuk keamanan finansial.",
                icon: ShieldCheck,
                link: "/products/protected-payment",
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <RevealItem key={p.slug}>
                  <SpotlightCard
                    spotlightColor="rgba(255, 255, 255, 0.08)"
                    className="h-full rounded-lg sm:rounded-2xl border border-white/[0.08] bg-[#0c0c11]/85 backdrop-blur-xl shadow-md transition-all duration-200 hover:border-white/25 hover:-translate-y-1 hover:shadow-[0_16px_32px_rgba(0,0,0,0.7)] active:scale-[0.98] overflow-hidden"
                  >
                    <Link
                      to={p.link}
                      data-testid={`landing-product-${p.slug}`}
                      className="group relative flex h-full flex-col justify-between p-2.5 sm:p-4 md:p-5"
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="font-gemini-mono text-[9.5px] sm:text-xs font-bold text-zinc-400">{p.num}</span>
                          <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-1.5 py-0.5 text-[8px] sm:text-[9.5px] font-semibold text-zinc-300">
                            {p.badge}
                          </span>
                        </div>
                        <div className="mt-2.5 sm:mt-4 flex items-center gap-1.5 sm:gap-2.5">
                          <div className="flex h-6 w-6 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-md sm:rounded-xl bg-white/[0.05] border border-white/[0.1] text-white">
                            <Icon size={12} className="sm:h-4 sm:w-4" />
                          </div>
                          <h3 className="text-xs sm:text-sm md:text-base font-bold text-white group-hover:text-zinc-100 transition-colors truncate">
                            {p.title}
                          </h3>
                        </div>
                        <p className="mt-1 sm:mt-1.5 text-[9.5px] sm:text-[11px] leading-snug text-zinc-400 line-clamp-3">
                          {p.desc}
                        </p>
                      </div>
                      <div className="mt-2.5 pt-2 border-t border-white/[0.06] flex items-center justify-between text-[9.5px] sm:text-xs font-semibold text-zinc-300 group-hover:text-white transition-colors">
                        <span>Eksplorasi</span>
                        <ArrowRight size={10} className="transition-transform group-hover:translate-x-1 sm:h-3 sm:w-3" />
                      </div>
                    </Link>
                  </SpotlightCard>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* 5. OKKAX COPILOT SPOTLIGHT STAGE */}
      <Reveal as="section" className="border-b border-white/[0.06] bg-transparent px-3.5 py-8 sm:px-6 sm:py-16 relative overflow-hidden font-gemini">
        <div className="mx-auto max-w-7xl relative z-10">
          <SpotlightCard
            spotlightColor="rgba(255, 46, 126, 0.1)"
            className="rounded-2xl sm:rounded-3xl border border-white/[0.12] bg-gradient-to-b from-[#14141f] to-[#0a0a0f] p-4 sm:p-8 lg:p-10 shadow-[0_32px_80px_rgba(0,0,0,0.9),inset_0_1px_0_rgba(255,255,255,0.08)]"
          >
            <div className="grid gap-6 sm:gap-8 lg:grid-cols-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.04] px-2.5 py-0.5 text-[10px] sm:text-xs font-bold text-zinc-300">
                  <Layers size={12} className="text-zinc-400" />
                  <span>Okkax Copilot</span>
                </div>
                <h2 className="editorial mt-3 text-xl sm:text-3xl lg:text-4xl text-white font-bold leading-tight">
                  Temui Okkax Copilot — Asisten Cerdas Operasional Acara.
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-zinc-300 sm:text-sm">
                  Okkax Copilot memahami seluruh data event, mengidentifikasi dependensi berisiko di Event Graph, menghitung alokasi anggaran dan break-even, hingga menyusun SOP gate check-in hari H.
                </p>
                <div className="mt-4 sm:mt-6 flex flex-wrap gap-2.5 sm:gap-3">
                  <Link
                    to="/okkax"
                    data-testid="landing-okkax-copilot-cta"
                    className="inline-flex items-center gap-2 rounded-xl bg-white hover:bg-zinc-200 px-4 py-2.5 sm:px-5 sm:py-3 text-xs sm:text-sm font-bold text-black transition-all shadow-[0_4px_24px_rgba(255,255,255,0.15)] active:scale-[0.98]"
                  >
                    <Terminal size={14} className="sm:h-4 sm:w-4" />
                    <span>Buka Command Center</span>
                    <ArrowRight size={13} className="sm:h-3.5 sm:w-3.5" />
                  </Link>
                  <Link
                    to="/products/copilot"
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/[0.15] bg-white/[0.04] px-3.5 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm font-semibold text-zinc-200 hover:border-white/[0.3] hover:text-white transition-all"
                  >
                    Pelajari Arsitektur AI
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-2">
                <div className="text-[10.5px] sm:text-xs font-bold uppercase tracking-wider text-zinc-400 mb-0.5 flex items-center gap-1.5 font-gemini-mono">
                  <Sparkles size={11} className="text-zinc-400" />
                  <span>Coba Tanyakan Langsung ke Okkax Copilot:</span>
                </div>
                <RevealGroup stagger={0.06} className="flex flex-col gap-2">
                  {[
                    { label: "Kalkulasi Budget Konser 5.000 pax", tag: "Context · Budget", prompt: "Hitung alokasi budget dan target tiket konser musik 5.000 pax Rp 1.25 Milyar" },
                    { label: "Deteksi Blocker di Event Graph", tag: "Intelligence · Graph", prompt: "Jelaskan struktur node Event Graph dan bagaimana menangani node yang statusnya Blocked" },
                    { label: "SOP Scanner Gate & Validasi QR", tag: "Action · Operations", prompt: "Bagaimana SOP validasi scanner tiket QR di gate dan pencegahan tiket ganda?" },
                    { label: "Valuasi Paket Sponsor Utama", tag: "Action · Commercial", prompt: "Bagaimana cara menentukan harga paket Presenting Sponsor dan hak aktivasi brand?" },
                  ].map((s, idx) => (
                    <RevealItem key={idx}>
                      <Link
                        to="/okkax"
                        data-testid={`okkax-landing-chip-${idx}`}
                        className="group rounded-lg sm:rounded-xl border border-white/[0.08] bg-[#0c0c11]/80 backdrop-blur-md p-2.5 sm:p-3 text-[11px] sm:text-xs text-zinc-300 hover:border-white/25 hover:text-white hover:bg-white/[0.04] transition-all flex items-center justify-between"
                      >
                        <span className="truncate pr-2 font-medium">→ {s.label}</span>
                        <ArrowRight size={12} className="text-zinc-400 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0" />
                      </Link>
                    </RevealItem>
                  ))}
                </RevealGroup>
              </div>
            </div>
          </SpotlightCard>
        </div>
      </Reveal>

      {/* 6. REGIONAL IMPACT & MULTI-CITY MAP STRIP */}
      <Reveal as="section" className="border-b border-white/[0.06] px-3.5 py-8 sm:px-6 sm:py-16 bg-transparent font-gemini">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col justify-between gap-3 md:flex-row md:items-end">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-0.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-300 backdrop-blur-md">
                <LineChart size={12} className="text-zinc-400" />
                <span>Regional Economic Multiplier</span>
              </div>
              <h2 className="editorial mt-2 sm:mt-3 text-xl sm:text-3xl text-white">
                Dampak ekonomi event di {networkTotals ? num(networkTotals.cities) : "seluruh"} kota Indonesia.
              </h2>
            </div>
            <Link
              to="/peta"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white transition-colors"
            >
              <span>Lihat Live Event Map Interaktif (/peta)</span>
              <ArrowRight size={12} />
            </Link>
          </div>

          <RevealGroup stagger={0.08} className="mt-5 sm:mt-8 grid grid-cols-2 gap-2.5 sm:gap-3.5 lg:grid-cols-4">
            <RevealItem>
              <SpotlightCard className="h-full rounded-xl sm:rounded-2xl border border-white/[0.08] bg-[#0c0c11]/85 backdrop-blur-xl p-3.5 sm:p-5 shadow-md">
                <div className="text-[9.5px] sm:text-xs uppercase tracking-wider text-zinc-400 font-gemini-mono">Total Aktivitas Event</div>
                <div className="font-gemini-mono mt-1.5 text-xl font-bold sm:text-2xl lg:text-3xl text-white">
                  {networkTotals ? compact(networkTotals.economic_activity) : "—"}
                </div>
              </SpotlightCard>
            </RevealItem>
            <RevealItem>
              <SpotlightCard className="h-full rounded-xl sm:rounded-2xl border border-white/[0.08] bg-[#0c0c11]/85 backdrop-blur-xl p-3.5 sm:p-5 shadow-md">
                <div className="text-[9.5px] sm:text-xs uppercase tracking-wider text-zinc-400 font-gemini-mono">Bisnis Teraktivasi</div>
                <div className="font-gemini-mono mt-1.5 text-xl font-bold sm:text-2xl lg:text-3xl text-white">
                  {networkTotals ? <CounterNumber value={networkTotals.businesses} suffix=" relasi" /> : "—"}
                </div>
              </SpotlightCard>
            </RevealItem>
            <RevealItem>
              <SpotlightCard className="h-full rounded-xl sm:rounded-2xl border border-white/[0.08] bg-[#0c0c11]/85 backdrop-blur-xl p-3.5 sm:p-5 shadow-md">
                <div className="text-[9.5px] sm:text-xs uppercase tracking-wider text-zinc-400 font-gemini-mono">Tenaga Kerja Terisi</div>
                <div className="font-gemini-mono mt-1.5 text-xl font-bold sm:text-2xl lg:text-3xl text-white">
                  {networkTotals ? <CounterNumber value={networkTotals.workforce_filled} suffix=" posisi" /> : "—"}
                </div>
              </SpotlightCard>
            </RevealItem>
            <RevealItem>
              <SpotlightCard className="h-full rounded-xl sm:rounded-2xl border border-white/[0.08] bg-[#0c0c11]/85 backdrop-blur-xl p-3.5 sm:p-5 shadow-md">
                <div className="text-[9.5px] sm:text-xs uppercase tracking-wider text-zinc-400 font-gemini-mono">Talent Terhubung</div>
                <div className="font-gemini-mono mt-1.5 text-xl font-bold sm:text-2xl lg:text-3xl text-white">
                  {networkTotals ? <CounterNumber value={networkTotals.talents} suffix=" talent" /> : "—"}
                </div>
              </SpotlightCard>
            </RevealItem>
          </RevealGroup>
          <p className="mt-3 text-[11px] text-zinc-400 font-gemini-mono">
            Model kalkulasi multiplier dampak ekonomi regional terhubung langsung dengan Live Event Map dan data Event Graph.
          </p>
        </div>
      </Reveal>

      {/* 7. SUBSCRIPTION PLANS (PRICING PREVIEW) */}
      <Reveal>
        <PricingPreview />
      </Reveal>

      {/* 8. FINALE FOOTER */}
      <Footer />
    </div>
  );
}
